import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, RefreshCw, ChevronLeft, ChevronRight, X,HandCoins,CreditCard,Check, } from 'lucide-react';
import api from '../../utils/api';

interface Technician {
  id: number;
  first_name: string;
  last_name: string;
}
interface OilChangeReservation {
  id: number;
  order_number: string;
  service_type: 'simple' | 'complete';
  oil_brand: string;
  scheduled_date: string;
  scheduled_time: string;
  location: string;
  status: string;
  transaction: string;
  oil_price : number;
  oil_filter_price : number;
  air_filter_price : number;
  labor_price : number;
  travel_price : number;
  total_price: number;
  payment_method : string;
  vehicle: {
    brand:  {
      name: string;
      icon: string;
    };
    modele: string;
  };
  client_name: string;
  phone: string;
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

export default function OilChangeReservationsList() {
  const [reservations, setReservations] = useState<OilChangeReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
const [selectedTechnicianId, setSelectedTechnicianId] = useState<number | null>(null);

  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0
  });
  const [selectedReservation, setSelectedReservation] = useState<OilChangeReservation | null>(null);
  const [toast, setToast] = useState({ visible: false, severity: '', message: '' });
  const { t } = useTranslation();

  const fetchTechnicians = async () => {
    try {
      const response = await api.get('/service-reservations/techniciens');
      setTechnicians(response.data);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      showToast('error', t('error_fetching_technicians'));
    }
  };
  useEffect(() => {
    fetchTechnicians();
  }, []);
  useEffect(() => {
    fetchReservations();
  }, [statusFilter, dateRange, globalFilter, pagination.current_page]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const params = {
        status: statusFilter,
        start_date: dateRange[0]?.toISOString().split('T')[0],
        end_date: dateRange[1]?.toISOString().split('T')[0],
        search: globalFilter,
        page: pagination.current_page,
        per_page: pagination.per_page
      };
      const response  = await api.get('/service-reservations', { params});


      setReservations(response.data.data);
      setPagination({
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        per_page: response.data.per_page,
        total: response.data.total,
        from: response.data.from,
        to: response.data.to
      });
    } catch (error) {
      console.error('Error fetching reservations:', error);
      showToast('error', t('error_fetching_orders'));
    } finally {
      setLoading(false);
    }
  };

  const showToast = (severity: string, message: string) => {
    setToast({ visible: true, severity, message });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  const statusOptions = [
    { label: t('all_statuses'), value: null },
    { label: t('pending'), value: 'pending' },
    { label: t('in_progress'), value: 'in_progress' },
    { label: t('completed'), value: 'completed' },
    { label: t('cancelled'), value: 'cancelled' },
  ];

  const resetFilters = () => {
    setStatusFilter(null);
    setDateRange([null, null]);
    setGlobalFilter('');
    setPagination(prev => ({ ...prev, current_page: 1 }));
    fetchReservations();
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const totalPages = pagination.last_page;
    const currentPage = pagination.current_page;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-3 py-1 rounded ${
              currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            {i}
          </button>
        );
      }
    } else {
      // First page
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={`px-3 py-1 rounded ${
            currentPage === 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
          }`}
        >
          1
        </button>
      );

      // Ellipsis and middle pages
      if (currentPage > 3) {
        buttons.push(<span key="ellipsis1" className="px-3 py-1">...</span>);
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(currentPage + 1, totalPages - 1);

      for (let i = start; i <= end; i++) {
        buttons.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-3 py-1 rounded ${
              currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            {i}
          </button>
        );
      }

      // Last page
      if (currentPage < totalPages - 2) {
        buttons.push(<span key="ellipsis2" className="px-3 py-1">...</span>);
      }

      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={`px-3 py-1 rounded ${
            currentPage === totalPages ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
          }`}
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  const openReservationDetails = (reservation: OilChangeReservation) => {
    setSelectedReservation(reservation);
    setSelectedTechnicianId(null);
  };

  const closeReservationDetails = () => {
    setSelectedReservation(null);
  };

  const handleCancelReservation = async () => {
    if (!selectedReservation) return;

    try {
      const response = await api.post(`service-reservations/${selectedReservation.order_number}/cancel`);
      showToast('success', t('cancellation_success'));
      fetchReservations();
      closeReservationDetails();
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      showToast('error', t('cancellation_error'));
    }
  };

  const handleAcceptReservation = async () => {
    if (!selectedReservation) return;

    try {
      const response = await api.post(`service-reservations/${selectedReservation.order_number}/accept`, {
        technicien : selectedTechnicianId
      });
      showToast('success', t('acceptation_success'));
      fetchReservations();
      closeReservationDetails();
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      showToast('error', t('cancellation_error'));
    }
  };

  const handleFinishReservation = async () => {
    if (!selectedReservation) return;

    try {
      const response = await api.post(`service-reservations/${selectedReservation.order_number}/finish`);
      showToast('success', t('finish_success'));
      fetchReservations();
      closeReservationDetails();
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      showToast('error', t('cancellation_error'));
    }
  };
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md z-50 ${
          toast.severity === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      {/* Page Header and Filters */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">{t('reservations_list')}</h2>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={t('search')}
            className="p-2 border rounded-md"
          />
          <select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value || null)}
            className="p-2 border rounded-md"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value || ''}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={dateRange[0]?.toISOString().split('T')[0] || ''}
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              const nextDay = new Date(selectedDate);
              nextDay.setDate(selectedDate.getDate() + 1);
              setDateRange([selectedDate, dateRange[1] ? dateRange[1] : nextDay]);
            }}
            className="p-2 border rounded-md"
          />
          <input
            type="date"
            value={dateRange[1]?.toISOString().split('T')[0] || ''}
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              const nextDay = new Date(selectedDate);
              nextDay.setDate(selectedDate.getDate() - 1);
              setDateRange([dateRange[0] ? dateRange[0] : nextDay, selectedDate]);
            }}
            className="p-2 border rounded-md"
          />
          <button
            onClick={resetFilters}
            className="p-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Reservations Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">{t('order_number')}</th>
                  <th className="py-3 px-6 text-left">{t('schadueled_at')}</th>
                  <th className="py-3 px-6 text-left">{t('vehicle')}</th>
                  <th className="py-3 px-6 text-left">{t('vidange_type')}</th>
                  <th className="py-3 px-6 text-left">{t('client')}</th>
                  <th className="py-3 px-6 text-left">{t('price')}</th>
                  <th className="py-3 px-6 text-center min-w-[150px]">{t('status')}</th>
                  <th className="py-3 px-6 text-center">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 text-sm font-light">
                {reservations.map((reservation) => (
                  <tr key={reservation.id} className="border-b border-gray-200 hover:bg-gray-100">
                    <td className="py-3 px-6 text-center ">
                      <div className='flex'>
                          {reservation.payment_method==="online" ? 
                          <div className='pr-2 flex'> 
                          
                          { reservation.payment_method==="online" && 
                          (reservation.transaction===null ? 
                                  <div className='absoslute    '><X color='#820007'   /> </div>:
                                        <div className='absoslute' ><Check color='#149636'   /> </div>)
                              } 
                            
                            <CreditCard  />  
                          </div>
                          
                          :
                          
                          <div className='pr-2'><HandCoins /></div>
                        }
                        {reservation.order_number}
                      </div>
                    </td>
                    <td className="py-3 px-6 text-left">
                      {new Date(reservation.scheduled_date).toLocaleDateString()} {reservation.scheduled_time}
                    </td>
                    <td className="py-3 px-6 text-left">
                      {reservation.vehicle?.brand?.name} {reservation.vehicle.modele}
                    </td>
                    <td className="py-3 px-6 text-left">
                      {t(`service_type_${reservation.service_type}`)}
                    </td>
                    <td className="py-3 px-6 text-center">{reservation.client_name}<br />{reservation.phone}<br />{reservation.location}</td>
                    <td className="py-3 px-6 text-left">{reservation.total_price} MAD</td>
                    <td className="py-3 px-6 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        reservation.status === 'completed' ? 'bg-green-200 text-green-800' :
                        reservation.status === 'in_progress' ? 'bg-blue-200 text-blue-800' :
                        reservation.status === 'cancelled' ? 'bg-red-200 text-red-800' :
                        'bg-yellow-200 text-yellow-800'
                      }`}>
                        {t(`${reservation.status}`)}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-center">
                      <button
                        onClick={() => openReservationDetails(reservation)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4">
            <div>
              {t('showing')} {pagination.from} - {pagination.to} {t('of')} {pagination.total}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {renderPaginationButtons()}
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50"
              >
                
<ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Reservation Details Modal */}
      {selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-bold">
                {t('reservation_details')}
              </h2>
              <button 
                onClick={closeReservationDetails}
                className="text-gray-600 hover:text-gray-900"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Reservation Overview */}
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">{t('order_number')}</span>
                  <span>{selectedReservation.order_number}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">{t('status')}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    selectedReservation.status === 'completed' ? 'bg-green-200 text-green-800' :
                    selectedReservation.status === 'in_progress' ? 'bg-blue-200 text-blue-800' :
                    selectedReservation.status === 'cancelled' ? 'bg-red-200 text-red-800' :
                    'bg-yellow-200 text-yellow-800'
                  }`}>
                    {t(`${selectedReservation.status}`)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="font-semibold">{t('payement')}</span>
                  <span className={`pl-2  py-1 rounded-full ${selectedReservation.payment_method === 'online'  ?'' : 'pr-2'} text-xs bg-blue-200 text-blue-800`}>
                    {t(`payment_${selectedReservation.payment_method}`)}

                      {  selectedReservation.payment_method === 'online' &&  
                      <span className={`px-2 py-1 ml-2 rounded-full text-xs ${
                        selectedReservation.transaction === null ? 'bg-red-200 text-blue-800' : 'bg-green-200 text-blue-800' 
                      }`}>
                        {t( selectedReservation.transaction === null ? 'unpaid' : 'paid')}
                      </span>}
                  </span>
                </div>
              </div>

              {/* Service Details */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg border-b pb-2">
                  {t('vidange_type')}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-600">{t('vidange_type')}</span>
                    <p className="font-medium">
                      {t(`service_type_${selectedReservation.service_type}`)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('oil_brand')}</span>
                    <p className="font-medium">{selectedReservation.oil_brand}</p>
                  </div>
                </div>
              </div>

              {/* Vehicle Details */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg border-b pb-2">
                  {t('vehicle_details')}
                </h3>
                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <span className="text-gray-600">{t('vehicle')}</span>
                    <p className="font-medium">
                      {selectedReservation.vehicle.brand.name} {selectedReservation.vehicle.modele}
                    </p>
                  </div>
                </div>
              </div>

              {/* Client Details */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg border-b pb-2">
                  {t('client')}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-600">{t('full_name')}</span>
                    <p className="font-medium">
                      {selectedReservation.client_name}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">{t('phone')}</span>
                    <p className="font-medium">
                      {selectedReservation.phone}
                    </p>
                  </div>
                </div>
              </div>

              {/* Scheduling Details */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg border-b pb-2">
                  {t('scheduling_details')}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-600">{t('schadueled_at')}</span>
                    <p className="font-medium">
                      {new Date(selectedReservation.scheduled_date).toLocaleDateString()} - {selectedReservation.scheduled_time}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <span className="text-gray-600">{t('location')}</span>
                    <p className="font-medium">{selectedReservation.location}</p>
                  </div>
                </div>
              </div>

              {/* Pricing Details */}
              <div className="space-y-2">
                <h3 className="font-semibold text-lg border-b pb-2">
                  {t('pricing_details')}
                </h3>
                <div className="bg-gray-100 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span>{t('oil_price')} <span className='font-bold'>{selectedReservation.oil_brand}</span></span>
                    <span className="">{selectedReservation.oil_price} MAD</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>{t('oil_filter_price')}</span>
                    <span className="">{selectedReservation.oil_filter_price} MAD</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>{t('air_filter_price')}</span>
                    <span className="">{selectedReservation.air_filter_price} MAD</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>{t('labor_price')}</span>
                    <span className="">{selectedReservation.labor_price} MAD</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>{t('travel_price')}</span>
                    <span className="">{selectedReservation.travel_price} MAD</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>{t('total_price')}</span>
                    <span className="font-bold">{selectedReservation.total_price} MAD</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className=" mt-4">
              {['pending'].includes(selectedReservation.status) && (
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            {t('select_technician')}
          </label>
          <select 
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            value={selectedTechnicianId || ''}
            onChange={(e) => setSelectedTechnicianId(Number(e.target.value))}
            required
          >
            <option value="">{t('select_technician')}</option>
            {technicians.map(tech => (
              <option key={tech.id} value={tech.id}>{tech.first_name} {tech.last_name}</option>
            ))}
          </select>
        </div>
      )}
              </div>
              <div className="flex space-x-2 mt-4">
                
                {['pending', 'in_progress'].includes(selectedReservation.status) && (
                  <button
                    onClick={handleCancelReservation}
                    className="flex-grow bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 transition-colors"
                  >
                    {t('cancel_reservation')}
                  </button>
                )}
                {['pending'].includes(selectedReservation.status) && (
                  <button
                    onClick={handleAcceptReservation}
                    className="flex-grow bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    {t('accept_reservation')}
                  </button>
                )}
                {['in_progress'].includes(selectedReservation.status) && (
                  <button
                    onClick={handleFinishReservation}
                    className="flex-grow bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    {t('finish_reservation')}
                  </button>
                )}
                <button
                  onClick={closeReservationDetails}
                  className="flex-grow bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}